// Grouping for the staffing matrices.
//
// Both the Modeled matrix and the post-automation matrix render rows of
// Discipline × Channel against columns of Level split On / Hub. The aggregation
// is identical; only the cell treatment differs (one is editable, the other
// tints automation impact), so this module owns the shape and each view owns its
// own rendering.

import type { DisciplineChannelCell, LevelId } from '@rc/engine/types';

/** Onshore vs hub (nearshore + offshore) headcount for one level. */
export interface LevelSplit {
  on: number;
  hub: number;
}

export interface ChannelRow {
  channelId: string;
  channelLabel: string;
  byLevel: Partial<Record<LevelId, LevelSplit>>;
  total: number;
  onshore: number;
  hub: number;
}

export interface DisciplineGroup {
  disciplineId: string;
  disciplineLabel: string;
  rows: ChannelRow[];
  total: number;
  onshore: number;
  hub: number;
  byLevel: Partial<Record<LevelId, LevelSplit>>;
}

export const MATRIX_EPS = 1e-6;

export function addSplit(
  bag: Partial<Record<LevelId, LevelSplit>>,
  lv: LevelId,
  on: number,
  hub: number,
) {
  const cur = bag[lv];
  if (cur) {
    cur.on += on;
    cur.hub += hub;
  } else {
    bag[lv] = { on, hub };
  }
}

export function levelTotal(s: LevelSplit | undefined): number {
  if (!s) return 0;
  return s.on + s.hub;
}

export function buildGroups(
  cells: DisciplineChannelCell[] | undefined,
  disciplineOrder: { id: string; label: string }[],
  channelOrder: { id: string; label: string }[],
  /** Rows that should exist even when they carry no FTE. Passing the modeled
   *  cells here keeps a row on screen after every one of its levels has been
   *  adjusted to zero — otherwise the row would vanish and take the inputs
   *  needed to undo the edit with it. */
  skeleton?: DisciplineChannelCell[],
): DisciplineGroup[] {
  if (!cells || cells.length === 0) return [];

  type Acc = {
    channelLabel: string;
    byLevel: Partial<Record<LevelId, LevelSplit>>;
    total: number;
    onshore: number;
    hub: number;
  };
  const byDisc = new Map<string, { label: string; channels: Map<string, Acc> }>();

  const bucketFor = (c: DisciplineChannelCell): Acc => {
    let discBucket = byDisc.get(c.disciplineId);
    if (!discBucket) {
      discBucket = { label: c.disciplineLabel, channels: new Map() };
      byDisc.set(c.disciplineId, discBucket);
    }
    let chBucket = discBucket.channels.get(c.channelId);
    if (!chBucket) {
      chBucket = {
        channelLabel:
          c.channelId === 'all'
            ? 'All channels'
            : c.channelLabel || c.channelId,
        byLevel: {},
        total: 0,
        onshore: 0,
        hub: 0,
      };
      discBucket.channels.set(c.channelId, chBucket);
    }
    return chBucket;
  };

  for (const c of skeleton || []) bucketFor(c);

  for (const c of cells) {
    const fte = Number(c.adjustedFte) || 0;
    if (Math.abs(fte) < MATRIX_EPS) continue;

    // Use the engine's per-cell shore split directly. It reflects the
    // scenario's per-discipline OGS config (hub, %, selected levels).
    // "Hub" combines the nearshore + offshore legs.
    const onFte = Number(c.onshoreFte) || 0;
    const hubFte = (Number(c.nearshoreFte) || 0) + (Number(c.offshoreFte) || 0);

    const chBucket = bucketFor(c);
    addSplit(chBucket.byLevel, c.levelId, onFte, hubFte);
    chBucket.total += fte;
    chBucket.onshore += onFte;
    chBucket.hub += hubFte;
  }

  const channelIndex = new Map(channelOrder.map((c, i) => [c.id, i]));
  const groups: DisciplineGroup[] = [];

  for (const disc of disciplineOrder) {
    const bucket = byDisc.get(disc.id);
    if (!bucket) continue;

    const rows: ChannelRow[] = Array.from(bucket.channels.entries())
      .map(([channelId, acc]) => ({
        channelId,
        channelLabel: acc.channelLabel,
        byLevel: acc.byLevel,
        total: acc.total,
        onshore: acc.onshore,
        hub: acc.hub,
      }))
      .sort((a, b) => {
        if (a.channelId === 'all' && b.channelId !== 'all') return -1;
        if (b.channelId === 'all' && a.channelId !== 'all') return 1;
        const ai = channelIndex.has(a.channelId)
          ? (channelIndex.get(a.channelId) as number)
          : Number.POSITIVE_INFINITY;
        const bi = channelIndex.has(b.channelId)
          ? (channelIndex.get(b.channelId) as number)
          : Number.POSITIVE_INFINITY;
        if (ai !== bi) return ai - bi;
        return a.channelLabel.localeCompare(b.channelLabel);
      });

    const totalByLevel: Partial<Record<LevelId, LevelSplit>> = {};
    let total = 0;
    let onshore = 0;
    let hub = 0;
    for (const r of rows) {
      total += r.total;
      onshore += r.onshore;
      hub += r.hub;
      for (const [lvl, split] of Object.entries(r.byLevel)) {
        if (!split) continue;
        addSplit(totalByLevel, lvl as LevelId, split.on, split.hub);
      }
    }

    groups.push({
      disciplineId: disc.id,
      disciplineLabel: bucket.label || disc.label,
      rows,
      total,
      onshore,
      hub,
      byLevel: totalByLevel,
    });
  }

  return groups;
}

/** Levels that carry headcount somewhere in the matrix, in model order. */
export function activeLevelsFor(
  groups: DisciplineGroup[],
  levelOrder: LevelId[],
): LevelId[] {
  const present = new Set<LevelId>();
  for (const g of groups) {
    for (const [lvl, split] of Object.entries(g.byLevel)) {
      if (levelTotal(split) > MATRIX_EPS) present.add(lvl as LevelId);
    }
  }
  return levelOrder.filter((lv) => present.has(lv));
}
